import { useEffect, useMemo, useRef, useState } from 'react';
import { useNotifications } from '@hooks/useNotifications';
import type { NotificationItem } from '@services/notificationsApi';
import './NotificationsBell.css';

interface NotificationsBellProps {
  autoRefreshMs?: number;
  // Optional presentational + callback hooks some portals pass. They are accepted for type
  // compatibility with existing call sites; the bell does not consume them today.
  theme?: string;
  onNewNotification?: (notification: NotificationItem) => void;
}

export default function NotificationsBell({ autoRefreshMs = 60_000 }: NotificationsBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);

  const {
    notifications,
    pagination,
    unreadCount,
    isLoading,
    error,
    loadPage,
    markRead,
    markAllRead
  } = useNotifications({ pageSize: 5, autoRefreshMs });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }

    return undefined;
  }, [isOpen]);

  const formattedNotifications = useMemo(
    () =>
      notifications.map((item) => ({
        ...item,
        timestamp: new Date(item.createdAt).toLocaleString()
      })),
    [notifications]
  );

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleMarkRead = async (id: string) => {
    await markRead(id);
  };

  const handleViewMore = async () => {
    if (pagination.page < pagination.totalPages) {
      await loadPage(pagination.page + 1);
    }
  };

  const unreadLabel = unreadCount > 99 ? '99+' : unreadCount.toString();

  return (
    <div className="notifications-wrapper">
      <button
        ref={toggleRef}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls="notifications-dropdown"
        className={`notifications-toggle ${isOpen ? 'open' : ''}`}
        onClick={toggleDropdown}
        title="Notifications"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
        {unreadCount > 0 && <span className="notifications-badge">{unreadLabel}</span>}
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          id="notifications-dropdown"
          role="dialog"
          aria-label="Notifications"
          className="notifications-dropdown"
        >
          <div className="notifications-header">
            <div>
              <h3>Notifications</h3>
              <p>{unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}</p>
            </div>
            {unreadCount > 0 && (
              <button className="mark-all-btn" onClick={() => markAllRead()}>
                Mark all read
              </button>
            )}
          </div>

          <div className="notifications-body">
            {isLoading && notifications.length === 0 ? (
              <div className="notifications-loading">Loading notifications…</div>
            ) : error && notifications.length === 0 ? (
              <div className="notifications-error">
                <p>{error}</p>
                <button onClick={() => loadPage(pagination.page)} className="retry-btn">
                  Retry
                </button>
              </div>
            ) : formattedNotifications.length === 0 ? (
              <div className="notifications-empty">
                <p>No notifications yet.</p>
              </div>
            ) : (
              <ul className="notifications-list">
                {formattedNotifications.map((notification) => (
                  <li
                    key={notification.id}
                    className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                  >
                    <div className="notification-content">
                      <div className="notification-title">{notification.title}</div>
                      <p className="notification-body">{notification.body}</p>
                      <span className="notification-timestamp">{notification.timestamp}</span>
                    </div>
                    {!notification.read && (
                      <button className="mark-read-btn" onClick={() => handleMarkRead(notification.id)}>
                        Mark read
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {pagination.page < pagination.totalPages && (
            <div className="notifications-footer">
              <button onClick={handleViewMore} className="view-more-btn" disabled={isLoading}>
                View older notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
